import { Link } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { UserContext } from "../context/UserContext";
import api from "../api";
import AvatarImg from "../components/AvatarImg";
import { EventsAPI } from "../api";
import { getLeaderboardRank } from "../utils/leaderboardUtils";
import { getSocialPlatforms, matchPlatform } from "../utils/socialPlatformUtils";

export default function Dashboard() {
  const { user, logout } = useContext(UserContext);
  const [points, setPoints] = useState({ lifetime: 0, weekly: 0 });
  const [badges, setBadges] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lifetimeRank, setLifetimeRank] = useState(null);
  const [weeklyRank, setWeeklyRank] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  // Stats viewer state
  const [statsSel, setStatsSel] = useState(null); // { platform: 'twitch', login, label }
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [statsTab, setStatsTab] = useState('overview'); // 'overview' | 'vods' | 'clips' | 'followers' | 'analytics'
  const [statsData, setStatsData] = useState(null); // { platform, login, vods, clips, user?, stream?, game? }
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followersError, setFollowersError] = useState('');
  const [followersPrevStack, setFollowersPrevStack] = useState([]); // stack of cursors for "Zurück"
  const [followersDebugOpen, setFollowersDebugOpen] = useState(false);
  const [twitchConn, setTwitchConn] = useState({ connected: null, login: null });
  const [youtubeConn, setYouTubeConn] = useState({ connected: null, title: null });
  const [ytAnalytics, setYtAnalytics] = useState(null);
  const [ytAnalyticsError, setYtAnalyticsError] = useState('');
  // Twitch analytics additions (subs, chatters)
  const [twSubsLoading, setTwSubsLoading] = useState(false);
  const [twSubsError, setTwSubsError] = useState('');
  const [twChattersLoading, setTwChattersLoading] = useState(false);
  const [twChattersError, setTwChattersError] = useState('');
  const [twRevenue, setTwRevenue] = useState(null);
  const [twRevenueError, setTwRevenueError] = useState('');
  const [twHype, setTwHype] = useState(null);
  const [twHypeError, setTwHypeError] = useState('');
  const [tiktokConn, setTikTokConn] = useState({ connected: null, name: null });
  const [ttAnalytics, setTtAnalytics] = useState(null);
  const [ttAnalyticsError, setTtAnalyticsError] = useState('');
  const [ttMoreLoading, setTtMoreLoading] = useState(false);
  const [instagramConn, setInstagramConn] = useState({ connected: null, username: null });
  const [igAnalytics, setIgAnalytics] = useState(null);
  const [igAnalyticsError, setIgAnalyticsError] = useState('');
  const [igMoreLoading, setIgMoreLoading] = useState(false);
  // My upcoming events
  const [myEvents, setMyEvents] = useState([]);
  const [myEventsLoading, setMyEventsLoading] = useState(false);
  const [myEventsError, setMyEventsError] = useState('');
  // Promo copy state
  const [promoCopied, setPromoCopied] = useState(false);
  const [promoCopyError, setPromoCopyError] = useState('');
  // Referral copy state
  const [refCopied, setRefCopied] = useState(false);
  const [refCopyError, setRefCopyError] = useState('');

  // Ensure relative image URLs are resolved against API ORIGIN (avoid appending '/api')
  const resolveImageUrl = (u) => {
    try {
      if (!u || typeof u !== 'string') return u;
      // Absolute or data URIs stay as-is
      if (/^(https?:)?\/\//i.test(u) || u.startsWith('data:')) return u;
      if (u.startsWith('/')) {
        const base = (api?.defaults?.baseURL || '').toString().replace(/\/$/, '');
        let origin = '';
        if (base) {
          try { origin = new URL(base).origin; } catch { origin = base; }
        }
        if (!origin && typeof window !== 'undefined' && window.location && window.location.origin) {
          origin = window.location.origin;
        }
        return origin ? origin + u : u;
      }
      return u;
    } catch { return u; }
  };

  // Twitch-specific helper to size box art templates like …/boxart/{width}x{height}.jpg
  const sizeTwitchBoxArt = (u, width = 144, height = 192) => {
    try {
      if (!u || typeof u !== 'string') return u;
      return u.replace(/\{width\}x\{height\}/g, `${width}x${height}`);
    } catch { return u; }
  };

  // Small date range formatter
  function formatDateRange(startAt, endAt) {
    try {
      const s = startAt ? new Date(startAt) : null;
      const e = endAt ? new Date(endAt) : null;
      if (!s && !e) return '';
      const fmt = (d) => d.toLocaleDateString(undefined, { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
      if (s && e) return `${fmt(s)} – ${fmt(e)}`;
      if (s) return `${fmt(s)}`;
      return `${fmt(e)}`;
    } catch { return ''; }
  }

  // Load "my upcoming events" for this user
  useEffect(() => {
    let cancelled = false;
    async function loadMyEvents() {
      if (!user?.id) return;
      setMyEventsLoading(true); setMyEventsError('');
      try {
        const { items } = await EventsAPI.myUpcoming({ limit: 8 });
        if (!cancelled) setMyEvents(Array.isArray(items) ? items : []);
      } catch (e) {
        if (!cancelled) setMyEventsError(e?.response?.data?.error || e?.message || 'Events konnten nicht geladen werden.');
      } finally {
        if (!cancelled) setMyEventsLoading(false);
      }
    }
    loadMyEvents();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    // Load badges and points
    api.get('/users/me/points').then(res => {
      if (!cancelled) {
        const p = (res && res.data) ? res.data : { lifetime: 0, weekly: 0 };
        if (typeof p.lifetime === 'number' || typeof p.weekly === 'number') setPoints({ lifetime: p.lifetime || 0, weekly: p.weekly || 0 });
      }
    }).catch(() => {});
    api.get(`/badges/user/${user.id}`).then(res => { if (!cancelled) setBadges(Array.isArray(res.data) ? res.data : []); }).catch(() => setBadges([]));
    // Load ranks for medals
    getLeaderboardRank(user.id, 'lifetime').then(r => { if (!cancelled) setLifetimeRank(r); }).catch(() => {});
    getLeaderboardRank(user.id, 'weekly').then(r => { if (!cancelled) setWeeklyRank(r); }).catch(() => {});
    // Unread chat count + poll like header
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/messages/conversations");
        if (cancelled) return;
        const count = (data || []).filter(c => c.lastMessage && !c.lastRead && Number(c.lastSenderId) !== Number(user.id)).length;
        setUnreadCount(count);
      } catch { if (!cancelled) setUnreadCount(0); }
    };
    fetchUnread();
    const timer = setInterval(fetchUnread, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user?.id]);

  // Load social platform registry for icon + matching
  useEffect(() => {
    let active = true;
    getSocialPlatforms().then(list => { if (active) setPlatforms(list || []); }).catch(() => setPlatforms([]));
    return () => { active = false; };
  }, []);

  // Helpers to classify social links by platform
  const userLinks = Array.isArray(user?.socialLinks) ? user.socialLinks : [];
  const getKey = (url, plat) => {
    const n = (plat?.name || '').toLowerCase();
    const u = (url || '').toLowerCase();
    if (n.includes('youtube') || /youtu\.be|youtube\.com/.test(u)) return 'youtube';
    if (n.includes('twitch') || /twitch\.tv/.test(u)) return 'twitch';
    if (n.includes('tiktok') || /tiktok\.com/.test(u)) return 'tiktok';
    if (n.includes('instagram') || /instagram\.com/.test(u)) return 'instagram';
    return null;
  };
  const classified = userLinks.map(l => {
    const plat = matchPlatform(l?.url, platforms);
    const key = getKey(l?.url, plat);
    return key ? { key, link: l, plat } : null;
  }).filter(Boolean);
  const ytLinks = classified.filter(x => x.key === 'youtube');
  const twLinks = classified.filter(x => x.key === 'twitch');
  const ttLinks = classified.filter(x => x.key === 'tiktok');
  const igLinks = classified.filter(x => x.key === 'instagram');

  // Parse twitch channel from URL
  function parseTwitchChannel(url) {
    try {
      if (!url) return null;
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      const parts = u.pathname.split('/').filter(Boolean);
      const first = parts[0]?.toLowerCase();
      if (!first) return null;
      if (["videos", "directory", "p"].includes(first)) return null;
      return first;
    } catch { return null; }
  }

  // ========== YouTube ==========
  function getYouTubeUrlFromLink(linkObj) {
    const u = linkObj?.link?.url || '';
    if (!u) return null;
    return u.startsWith('http') ? u : (`https://${u}`);
  }

  async function handleYouTubeStatsClick(linkObj) {
    const url = getYouTubeUrlFromLink(linkObj);
    if (!url) return;
    setStatsSel({ platform: 'youtube', url, label: linkObj?.link?.name || 'YouTube' });
    setStatsTab('overview');
    setStatsLoading(true);
    setStatsError('');
    setStatsData(null);
    try {
      // Prefer OAuth-based recent videos if the user connected YouTube; fallback to URL-based endpoint
      const chP = api.get('/social-content/youtube/channel-stats', { params: { url } }).catch(() => ({ data: {} }));
      let vidsP;
      if (youtubeConn?.connected) {
        vidsP = api.get('/social-content/me/youtube/recent-videos', { params: { first: 12 } })
          .then(r => (r && Array.isArray(r.data?.items) && r.data.items.length > 0) ? r : api.get('/social-content/youtube/recent-videos', { params: { url, first: 12 } }))
          .catch(() => ({ data: { items: [] } }));
      } else {
        vidsP = api.get('/social-content/youtube/recent-videos', { params: { url, first: 12 } }).catch(() => ({ data: { items: [] } }));
      }
      const [ch, vids] = await Promise.all([chP, vidsP]);
      const channelStats = ch?.data || {};
      const recent = Array.isArray(vids?.data?.items) ? vids.data.items : [];
      setStatsData({ platform: 'youtube', url, channelStats, recent });
    } catch {
      setStatsError('YouTube-Daten konnten nicht geladen werden.');
    } finally {
      setStatsLoading(false);
    }
  }

  async function handleTwitchStatsClick(linkObj) {
    const login = parseTwitchChannel(linkObj?.link?.url);
    if (!login || !user?.id) return;
    setStatsSel({ platform: 'twitch', login, label: linkObj?.link?.name || login });
    setStatsTab('overview');
    setStatsLoading(true);
    setStatsError('');
    setStatsData(null);
    try {
      // Fetch aggregated social content for this user and filter for the selected Twitch channel
      const { data } = await api.get(`/social-content/user/${user.id}`);
      const groups = Array.isArray(data?.twitchByChannel) ? data.twitchByChannel : [];
      const group = groups.find(g => (g?.login || '').toLowerCase() === login.toLowerCase());
      const vods = (group?.vods || data?.twitch?.vods || []).map(v => ({ ...v, thumbnailUrl: resolveImageUrl(v.thumbnailUrl) }));
      const clips = (group?.clips || data?.twitch?.clips || []).map(c => ({ ...c, thumbnailUrl: resolveImageUrl(c.thumbnailUrl) }));
      // Optional enriched fields from backend
      const userInfo = group?.user || data?.twitch?.user || null;
      const stream = group?.stream || data?.twitch?.stream || null;
      const game = group?.game || data?.twitch?.game || null;
      // Resolve possible images
      const sizedGame = game ? { ...game, box_art_url: sizeTwitchBoxArt(game.box_art_url || game.boxArtUrl || '') } : null;
      const safeUser = userInfo ? { ...userInfo, profile_image_url: resolveImageUrl(userInfo.profile_image_url || userInfo.profileImageUrl || '') } : null;
      setStatsData({ platform: 'twitch', login, vods, clips, user: safeUser, stream, game: sizedGame, followers: null });
    } catch (e) {
      setStatsError('Twitch-Daten konnten nicht geladen werden.');
    } finally {
      setStatsLoading(false);
    }
  }

  // ========== TikTok ==========
  async function handleTikTokStatsClick(linkObj) {
    if (!user?.id) return;
    setStatsSel({ platform: 'tiktok', label: linkObj?.link?.name || 'TikTok' });
    setStatsTab('overview');
    setStatsLoading(true);
    setStatsError('');
    setStatsData(null);
    try {
      // Try OAuth user-scoped endpoint first; fallback to scrape-based endpoint
      let videos = [];
      try {
        const params = new URLSearchParams();
        params.set('first', '20');
        const me = await api.get(`/social-content/me/tiktok/videos?` + params.toString());
        const items = Array.isArray(me?.data?.items) ? me.data.items : [];
        videos = items;
        const cursor = (typeof me?.data?.cursor !== 'undefined') ? me.data.cursor : null;
        const hasMore = Boolean(me?.data?.has_more);
        const safe = (videos || []).map(v => ({ ...v, thumbnailUrl: resolveImageUrl(v.thumbnailUrl) }));
        setStatsData({ platform: 'tiktok', videos: safe, pagination: { cursor, has_more: hasMore } });
        setStatsLoading(false);
        return; // early return as we've already set state
      } catch {}
      if (!Array.isArray(videos) || videos.length === 0) {
        const { data } = await api.get(`/social-content/user/${user.id}/tiktok`);
        const vids = Array.isArray(data?.tiktok?.videos) ? data.tiktok.videos : [];
        videos = vids;
      }
      const safe = (videos || []).map(v => ({ ...v, thumbnailUrl: resolveImageUrl(v.thumbnailUrl) }));
      setStatsData({ platform: 'tiktok', videos: safe, pagination: { cursor: null, has_more: false } });
    } catch (e) {
      setStatsError('TikTok-Daten konnten nicht geladen werden.');
    } finally {
      setStatsLoading(false);
    }
  }

  async function loadMoreTikTokVideos() {
    if (!statsData || statsSel?.platform !== 'tiktok') return;
    const cursor = statsData?.pagination?.cursor;
    if (cursor === null || typeof cursor === 'undefined') return;
    setTtMoreLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('first', '20');
      params.set('cursor', String(cursor));
      const me = await api.get(`/social-content/me/tiktok/videos?` + params.toString());
      const items = Array.isArray(me?.data?.items) ? me.data.items : [];
      const safeNew = items.map(v => ({ ...v, thumbnailUrl: resolveImageUrl(v.thumbnailUrl) }));
      const cursorNext = (typeof me?.data?.cursor !== 'undefined') ? me.data.cursor : null;
      const hasMore = Boolean(me?.data?.has_more);
      setStatsData(prev => ({ ...(prev||{}), videos: [ ...(prev?.videos||[]), ...safeNew ], pagination: { cursor: cursorNext, has_more: hasMore } }));
    } catch {}
    finally {
      setTtMoreLoading(false);
    }
  }

  // ========== Instagram ==========
  async function handleInstagramStatsClick(linkObj) {
    if (!user?.id) return;
    setStatsSel({ platform: 'instagram', label: linkObj?.link?.name || 'Instagram' });
    setStatsTab('overview');
    setStatsLoading(true);
    setStatsError('');
    setStatsData(null);
    try {
      const [pr, med] = await Promise.all([
        api.get('/social-content/me/instagram/profile').catch(() => ({ data: {} })),
        api.get('/social-content/me/instagram/media', { params: { first: 20 } }).catch(() => ({ data: { items: [] } })),
      ]);
      const profile = pr?.data?.profile || null;
      const items = Array.isArray(med?.data?.items) ? med.data.items : [];
      const after = (typeof med?.data?.after !== 'undefined') ? med.data.after : null;
      const hasMore = Boolean(med?.data?.has_more);
      const safe = (items || []).map(m => ({ ...m, thumbnailUrl: resolveImageUrl(m.thumbnailUrl) }));
      setStatsData({ platform: 'instagram', profile, media: safe, pagination: { after, has_more: hasMore } });
    } catch (e) {
      setStatsError('Instagram-Daten konnten nicht geladen werden.');
    } finally {
      setStatsLoading(false);
    }
  }

  async function loadMoreInstagramMedia() {
    if (!statsData || statsSel?.platform !== 'instagram') return;
    const after = statsData?.pagination?.after;
    if (after === null || typeof after === 'undefined') return;
    setIgMoreLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('first', '20');
      params.set('after', String(after));
      const me = await api.get(`/social-content/me/instagram/media?` + params.toString());
      const items = Array.isArray(me?.data?.items) ? me.data.items : [];
      const safeNew = items.map(m => ({ ...m, thumbnailUrl: resolveImageUrl(m.thumbnailUrl) }));
      const afterNext = (typeof me?.data?.after !== 'undefined') ? me.data.after : null;
      const hasMore = Boolean(me?.data?.has_more);
      setStatsData(prev => ({ ...(prev||{}), media: [ ...(prev?.media||[]), ...safeNew ], pagination: { after: afterNext, has_more: hasMore } }));
    } catch {}
    finally {
      setIgMoreLoading(false);
    }
  }

  // Followers fetcher with pagination
  async function fetchTwitchFollowers(afterCursor) {
    if (!statsSel?.login || !user?.id) return;
    setFollowersLoading(true);
    setFollowersError('');
    try {
      let data;
      if (twitchConn.connected === true && statsSel?.login && twitchConn.login && String(twitchConn.login).toLowerCase() === String(statsSel.login).toLowerCase()) {
        // Use broadcaster-scoped endpoint when OAuth is connected
        const params = new URLSearchParams();
        params.set('first', '25');
        params.set('stats', '1');
        if (afterCursor) params.set('after', afterCursor);
        const resp = await api.get(`/social-content/me/twitch/followers?` + params.toString());
        data = resp.data;
      } else {
        // Fallback to app-token follower listing by channel login
        const params = new URLSearchParams();
        params.set('login', statsSel.login);
        params.set('first', '25');
        if (afterCursor) params.set('after', afterCursor);
        const resp = await api.get(`/social-content/user/${user.id}/twitch/followers?` + params.toString());
        data = resp.data;
      }
      setStatsData(prev => ({ ...(prev||{}), followers: data || { total: 0, items: [], recent: { last7: 0, last30: 0 }, pagination: null } }));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Follower konnten nicht geladen werden.';
      setFollowersError(String(msg));
    } finally {
      setFollowersLoading(false);
    }
  }

  // Load Twitch connection status once to decide which endpoint to use for followers
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/integrations/status');
        if (!cancelled) {
          const tw = res?.data?.twitch || {};
          setTwitchConn({ connected: !!tw.connected, login: tw.login || null });
          const yt = res?.data?.youtube || {};
          setYouTubeConn({ connected: !!yt.connected, title: yt.title || null });
          const tk = res?.data?.tiktok || {};
          setTikTokConn({ connected: !!tk.connected, name: tk.display_name || tk.open_id || null });
          const ig = res?.data?.instagram || {};
          setInstagramConn({ connected: !!ig.connected, username: ig.username || null });
        }
      } catch {
        if (!cancelled) { setTwitchConn({ connected: false, login: null }); setYouTubeConn({ connected: false, title: null }); setTikTokConn({ connected: false, name: null }); setInstagramConn({ connected: false, username: null }); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle tab changes: load followers on first open
  function onChangeTab(t) {
    setStatsTab(t);
    if (t === 'followers' && statsSel?.platform === 'twitch') {
      if (!statsData?.followers) {
        setFollowersPrevStack([]);
        fetchTwitchFollowers(undefined);
      }
    }
    if (t === 'analytics' && statsSel?.platform === 'twitch') {
      // Lazy-load subs/chatters once
      if (!statsData?.subs || !statsData?.chatters) {
        fetchTwitchAnalytics();
      }
    }
    if (t === 'analytics' && statsSel?.platform === 'tiktok') {
      if (!ttAnalytics && tiktokConn.connected) {
        fetchTikTokAnalytics();
      }
    }
    if (t === 'analytics' && statsSel?.platform === 'instagram') {
      if (!igAnalytics && instagramConn.connected) {
        fetchInstagramAnalytics();
      }
    }
  }

  async function fetchTwitchAnalytics() {
    setTwSubsLoading(true); setTwSubsError('');
    setTwChattersLoading(true); setTwChattersError('');
    setTwRevenue(null); setTwRevenueError(''); setTwHype(null); setTwHypeError('');
    try {
      const [subsResp, chatResp, revResp, hypeResp] = await Promise.all([
        api.get('/social-content/me/twitch/subscriptions').catch(e => ({ data: { total: 0, items: [] }, _err: e })),
        api.get('/social-content/me/twitch/chatters').catch(e => ({ data: { total: 0, items: [] }, _err: e })),
        api.get('/social-content/me/twitch/revenue-estimate').catch(e => ({ data: null, _err: e })),
        api.get('/social-content/me/twitch/hype-train').catch(e => ({ data: null, _err: e })),
      ]);
      const subs = subsResp?.data || { total: 0, items: [] };
      const chatters = chatResp?.data || { total: 0, items: [] };
      setStatsData(prev => ({ ...(prev||{}), subs, chatters }));
      setTwRevenue(revResp?.data || null);
      setTwHype(hypeResp?.data || null);
    } catch (e) {
      setTwSubsError('Abos konnten nicht geladen werden.');
      setTwChattersError('Chatter konnten nicht geladen werden.');
      setTwRevenueError('Einnahmen-Schätzung nicht verfügbar.');
      setTwHypeError('Hype Train Daten nicht verfügbar.');
    } finally {
      setTwSubsLoading(false);
      setTwChattersLoading(false);
    }
  }

  async function fetchTikTokAnalytics() {
    setTtAnalyticsError('');
    setTtAnalytics(null);
    try {
      const { data } = await api.get('/social-content/me/tiktok/analytics/overview');
      setTtAnalytics(data || null);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'TikTok Analytics nicht verfügbar.';
      setTtAnalyticsError(String(msg));
    }
  }

  async function fetchInstagramAnalytics() {
    setIgAnalyticsError('');
    setIgAnalytics(null);
    try {
      const { data } = await api.get('/social-content/me/instagram/analytics/overview');
      setIgAnalytics(data || null);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Instagram Analytics nicht verfügbar.';
      setIgAnalyticsError(String(msg));
    }
  }

  const memberSince = useMemo(() => {
    try {
      if (!user?.createdAt) return null;
      const d = new Date(user.createdAt);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString();
    } catch { return null; }
  }, [user?.createdAt]);
  return (
    <div className="home-page">
      <section style={{width:'100vw', margin:0, padding:0, position:'relative', left:'-120px'}}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Dashboard</h2>
        <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:18, marginBottom:12}}>
          {/* avatar with env-aware resolver, cache-busting and onError fallback */}
          <AvatarImg src={user?.avatarUrl ? (user.avatarUrl + (user.avatarUrl.includes('?') ? '' : ('?t=' + Date.now()))) : '/avatars/default-avatar.png'} alt="Avatar" size={54} style={{ border:'2px solid #32CD89' }} />
          <span style={{fontSize:'1.2em', fontWeight:500}}>{user?.displayName || user?.username}</span>
        </div>
        <div className="hero-desc" style={{margin:'0 auto 24px', textAlign:'center', maxWidth:760}}>
          <div>Willkommen zurück, <b>{user?.displayName || user?.username}</b>!</div>
          <div style={{opacity:.9, marginTop:4}}>
            Mitglied seit {memberSince || '—'}
            {Array.isArray(user?.roles) && user.roles.length > 0 && (
              <span style={{marginLeft:10}}>· Rollen: {user.roles.map(r => (
                <span key={r} style={{background:'rgba(50,205,137,0.12)', color:'#32CD89', borderRadius:12, padding:'2px 10px', marginRight:6, fontWeight:500}}>{r}</span>
              ))}</span>
            )}
          </div>
          <div style={{marginTop:6, fontSize:16}}>
            <span style={{marginRight:18}}>
              Lifetime‑Punkte: <span style={{color:'#32CD89', fontWeight:700}}>{points.lifetime}</span>
              {lifetimeRank === 1 && <span title="1. Platz" style={{marginLeft:8,fontSize:22}}>🥇</span>}
              {lifetimeRank === 2 && <span title="2. Platz" style={{marginLeft:8,fontSize:22}}>🥈</span>}
              {lifetimeRank === 3 && <span title="3. Platz" style={{marginLeft:8,fontSize:22}}>🥉</span>}
            </span>
            <span>
              Wöchentliche Punkte: <span style={{color:'#32CD89', fontWeight:700}}>{points.weekly}</span>
              {weeklyRank === 1 && <span title="1. Platz" style={{marginLeft:8,fontSize:22}}>🥇</span>}
              {weeklyRank === 2 && <span title="2. Platz" style={{marginLeft:8,fontSize:22}}>🥈</span>}
              {weeklyRank === 3 && <span title="3. Platz" style={{marginLeft:8,fontSize:22}}>🥉</span>}
            </span>
          </div>
          <div style={{marginTop:8}}>
            <span style={{fontWeight:600}}>Abzeichen:</span>{' '}
            {badges.length === 0 ? <span style={{color:'#888'}}>Noch keine Abzeichen.</span> : (
              <span style={{display:'inline-flex', gap:8, flexWrap:'wrap', alignItems:'center'}}>
                {[...badges]
                  .filter(ub => ub && ub.Badge)
                  .sort((a, b) => (a.Badge?.sortIndex ?? 100) - (b.Badge?.sortIndex ?? 100))
                  .slice(0,6)
                  .map(ub => {
                    const levels = Array.isArray(ub.Badge?.BadgeLevels) ? ub.Badge.BadgeLevels : [];
                    let userLevel = null;
                    if (ub.note && ub.note.startsWith('Level ')) {
                      const lvlNum = parseInt(ub.note.replace('Level ', ''), 10);
                      userLevel = levels.find(l => l.level === lvlNum) || null;
                    }
                    const icon = resolveImageUrl((userLevel && userLevel.icon) || ub.Badge.icon);
                    const title = (userLevel?.description) || ub.Badge.name;
                    return (
                      <img key={ub.id} src={icon} alt={ub.Badge.name} title={title} style={{width:24, height:24}} />
                    );
                  })}
                {badges.length > 6 && <span style={{opacity:.8}}>+{badges.length - 6} weitere</span>}
              </span>
            )}
          </div>
        </div>
      </section>

      <section style={{width:'100%',maxWidth:'1200px',margin:'0 auto',padding:0}}>
        <div className="highlight-card" style={{gap:18, padding:32, textAlign:'center'}}>
          <h2 className="highlight-title" style={{marginBottom:8}}>Deine Aktionen</h2>
          <div style={{display:"flex", gap:18, justifyContent:'center', flexWrap:'wrap', marginBottom:12}}>
            <Link className="hero-btn-secondary" style={{padding:"14px 28px", fontSize:"1rem"}} to={`/supportplan?view=week&scope=personal&userId=${encodeURIComponent(user?.id||'me')}`}>Eigenen Streamplan öffnen</Link>
            <Link className="hero-btn-secondary" style={{position:'relative', padding:"14px 28px", fontSize:"1rem"}} to="/chat">
              Privater-Chat öffnen
              {unreadCount > 0 && (
                <span style={{
                  position:'absolute', top:-8, right:-12, background:'#2ecc40', color:'#fff', borderRadius:12, fontSize:13, fontWeight:700, minWidth:22, height:22, display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'0 7px', boxShadow:'0 2px 8px #0002', border:'2px solid #fff'
                }}>{unreadCount}</span>
              )}
            </Link>
            <Link className="hero-btn-secondary" style={{padding:"14px 28px", fontSize:"1rem"}} to="/integrations">
              Kanal‑Verknüpfungen
            </Link>
            <button className="hero-btn-primary" style={{padding:"14px 28px", fontSize:"1rem"}} onClick={logout}>Logout</button>
          </div>
        </div>
      </section>

      {/* Deine Events */}
      <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
        <div className="highlight-card" style={{gap:18, padding:24}}>
          <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:8, marginBottom:8}}>
            <h2 className="highlight-title" style={{margin:0}}>Deine Events</h2>
            <Link to="/events" className="hero-btn-secondary" style={{padding:'8px 14px'}}>Alle Events ansehen</Link>
          </div>
          {myEventsLoading && (
            <div style={{padding:8, color:'#9aa3b2'}}>Lädt …</div>
          )}
          {!myEventsLoading && myEventsError && (
            <div style={{padding:8, color:'#ff8484'}}>{myEventsError}</div>
          )}
          {!myEventsLoading && !myEventsError && (
            <div>
              {myEvents.length === 0 ? (
                <div style={{color:'#9aa3b2'}}>Keine anstehenden Events.</div>
              ) : (
                <div className="my-events-grid">
                  {myEvents.map(ev => (
                    <Link key={ev.id} to={`/events/${ev.id}`} style={{textDecoration:'none', color:'#fff'}}>
                      <div style={{border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, overflow:'hidden', background:'rgba(255,255,255,0.04)'}}>
                        <div style={{position:'relative', height:140, background:'#0f2a56'}}>
                          {ev.posterUrl && (
                            <img src={resolveImageUrl(ev.posterUrl)} alt="Poster" style={{width:'100%', height:'100%', objectFit:'cover', display:'block'}} />
                          )}
                          <span style={{position:'absolute', top:8, left:8, background:'rgba(0,0,0,0.55)', border:'1px solid rgba(255,255,255,0.25)', borderRadius:999, padding:'3px 8px', fontSize:12, fontWeight:700}}>
                            {ev.myStatus === 'owner' ? 'Veranstalter' : (ev.myStatus === 'confirmed' ? 'Angemeldet' : (ev.myStatus === 'waitlisted' ? 'Warteliste' : 'Interessiert'))}
                          </span>
                        </div>
                        <div style={{padding:10}}>
                          <div style={{fontWeight:800, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{ev.title}</div>
                          <div style={{fontSize:12, opacity:.8, marginTop:2}}>{formatDateRange(ev.startAt, ev.endAt)}</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Deine Außenwerbung */}
      <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
        <div className="highlight-card" style={{gap:18, padding:24}}>
          <h2 className="highlight-title" style={{marginBottom:8}}>Deine Außenwerbung & Referral</h2>
          <div style={{color:'#d6f3e5', opacity:.95, marginBottom:10}}>
            Nutze dein persönliches Banner als Einbettung (z. B. Twitch‑Profilkachel) oder als OBS‑Overlay. Es aktualisiert sich automatisch, wenn du z.B. deinen Anzeigenamen oder Profilbild änderst.
          </div>
          {(() => {
            try {
              // Prefer full API base (including path like /api) to work behind proxies; fallback to window origin
              let base = '';
              try { base = (api?.defaults?.baseURL || '').toString().replace(/\/$/, ''); } catch {}
              if (!base && typeof window !== 'undefined' && window.location && window.location.origin) {
                base = window.location.origin;
              }
              const prefix = base || '';
              const svgUrl = `${prefix}/promo/u/${encodeURIComponent(user?.username||'me')}.svg`;
              const pngUrl = `${prefix}/promo/u/${encodeURIComponent(user?.username||'me')}.png`;

              const presetDefs = [
                { key: 'portrait', label: 'Twitch Panel Grafik (320×600)', previewQuery: '?w=360&h=675' },
                { key: 'banner', label: 'Banner (1200×400)', previewQuery: '' },
                { key: 'twitch', label: 'Twitch (1200×480)', previewQuery: '' },
                // keep dashboard lightweight: preview YouTube smaller, but copy URL stays at native preset size
                { key: 'youtube', label: 'YouTube Channel Art (2560×1440)', previewQuery: '?w=1280&h=720' },
                { key: 'square', label: 'Quadrat (1080×1080)', previewQuery: '?w=720&h=720' },

                // creative extras
                { key: 'story', label: 'Story (1080×1920)', previewQuery: '?w=360&h=640' },
                { key: 'og', label: 'Share Card (1200×630)', previewQuery: '?w=600&h=315' },
                { key: 'widehd', label: 'HD Wallpaper (1920×1080)', previewQuery: '?w=640&h=360' },
                { key: 'badge', label: 'Badge (800×800)', previewQuery: '?w=520&h=520' },
                { key: 'card', label: 'Header Card (1024×512)', previewQuery: '?w=640&h=320' },

                // extremes + retro
                { key: 'extreme-min', label: 'Extreme: Minimal (1080×1350)', previewQuery: '?w=360&h=450' },
                { key: 'extreme-acid', label: 'Extreme: Acid (1600×900)', previewQuery: '?w=640&h=360' },
                { key: 'pixel', label: 'Retro Pixel (1080×1080)', previewQuery: '?w=520&h=520' },
                { key: 'pixelwide', label: 'Retro Pixel Wide (1200×400)', previewQuery: '?w=600&h=200' },
                { key: 'pixelpanel', label: 'Retro Pixel Panel (640×360)', previewQuery: '?w=640&h=360' },
                { key: 'futurwall', label: 'Futuristic Wallpaper (2560×1440)', previewQuery: '?w=640&h=360' },
              ];

              const presetUrls = presetDefs.map(p => {
                const baseUserPath = `${prefix}/promo/u/${encodeURIComponent(user?.username||'me')}`;
                const basePath = p.key === 'portrait'
                  ? baseUserPath
                  : `${baseUserPath}/${p.key}`;
                return {
                  ...p,
                  svg: `${basePath}.svg`,
                  png: `${basePath}.png`,
                  previewPng: `${basePath}.png${p.previewQuery || ''}`,
                };
              });

              const copy = async (txt) => {
                setPromoCopyError('');
                try {
                  await navigator.clipboard.writeText(txt);
                  setPromoCopied(true);
                  setTimeout(() => setPromoCopied(false), 1800);
                } catch {
                  setPromoCopyError('Konnte nicht kopieren.');
                }
              };
              // Referral-Link sollte NIEMALS /api/ enthalten, sondern Nutzer direkt auf die SPA /register Seite führen
              let originOnly = '';
              if (base) {
                try { originOnly = new URL(base).origin; } catch { originOnly = base.replace(/\/?api$/,''); }
              }
              if (!originOnly && typeof window !== 'undefined' && window.location?.origin) {
                originOnly = window.location.origin;
              }
              const refLink = `${originOnly}/register?ref=${encodeURIComponent(user?.id||'')}`;
              return (
                <div>
                  <div style={{display:'flex', flexDirection:'column', gap:12, width:'100%', marginBottom:10}}>
                    <div style={{display:'flex', flexDirection:'column', gap:6, width:'100%'}}>
                      <div style={{marginTop:0, padding:'10px 12px', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, background:'rgba(255,255,255,0.04)'}}>
                        <div style={{fontWeight:700, marginBottom:8}}>Grafiken (Vorschau + Links)</div>
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:12, width:'100%'}}>
                          {presetUrls.map(p => (
                            <div key={p.key} style={{border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, overflow:'hidden', background:'rgba(255,255,255,0.03)'}}>
                              <div style={{padding:'10px 10px 8px 10px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:10}}>
                                <div style={{fontWeight:800, fontSize:13}}>{p.label}</div>
                                <a href={p.png} target="_blank" rel="noreferrer" style={{fontSize:12, color:'#9ddfbe', textDecoration:'none'}}>Öffnen</a>
                              </div>
                              <div style={{background:'rgba(0,0,0,0.18)', borderTop:'1px solid rgba(255,255,255,0.10)', borderBottom:'1px solid rgba(255,255,255,0.10)'}}>
                                <img src={p.previewPng} alt={p.label} style={{width:'100%', height:140, objectFit:'contain', display:'block'}} />
                              </div>
                              <div style={{padding:10, display:'flex', flexDirection:'column', gap:8}}>
                                <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                                  <button className="hero-btn-secondary" style={{padding:'6px 10px'}} onClick={async () => { await copy(p.svg); }}>SVG kopieren</button>
                                  <button className="hero-btn-secondary" style={{padding:'6px 10px'}} onClick={async () => { await copy(p.png); }}>PNG kopieren</button>
                                </div>
                                <div style={{fontSize:11, opacity:.7}}>
                                  <div style={{marginBottom:4}}><span style={{opacity:.8}}>SVG:</span> <code style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.10)', padding:'2px 6px', borderRadius:8}}>{p.svg}</code></div>
                                  <div><span style={{opacity:.8}}>PNG:</span> <code style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.10)', padding:'2px 6px', borderRadius:8}}>{p.png}</code></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div style={{marginTop:10, fontSize:11, opacity:.65}}>
                          Tipp: Weitere Formate sind verfügbar via Endung (.webp, .jpg) – z.B. <code>/banner.webp</code>.
                        </div>
                      </div>
                      <div style={{fontSize:11, opacity:.65}}>PNG akzeptiert von Plattformen die kein SVG zulassen. Größe anpassbar per Query: <code>?w=1600&h=600</code></div>
                      <div style={{marginTop:12, padding:'10px 12px', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, background:'rgba(255,255,255,0.04)', display:'flex', flexDirection:'column', gap:8}}>
                        <div style={{fontWeight:700}}>Referral‑Link</div>
                        <div style={{fontSize:12, opacity:.8}}>Nutze diesen Link, um neue Mitglieder zu werben. Die Registrierung wird dir zugeordnet.</div>
                        <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                          <code style={{background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', padding:'4px 8px', borderRadius:8, fontSize:12}}>{refLink}</code>
                          <button
                            className="hero-btn-secondary"
                            style={{padding:'6px 10px'}}
                            onClick={async () => {
                              setRefCopyError('');
                              try { await navigator.clipboard.writeText(refLink); setRefCopied(true); setTimeout(()=>setRefCopied(false),1800); } catch { setRefCopyError('Konnte nicht kopieren.'); }
                            }}
                          >Referral kopieren</button>
                          {refCopied && <span style={{color:'#32CD89', fontWeight:700}}>Kopiert ✓</span>}
                          {refCopyError && <span style={{color:'#ff8484'}}>{refCopyError}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{display:'flex', gap:10, flexWrap:'wrap', alignItems:'center'}}>
                      {promoCopied && <span style={{color:'#32CD89', fontWeight:700}}>Kopiert ✓</span>}
                      {promoCopyError && <span style={{color:'#ff8484'}}>{promoCopyError}</span>}
                    </div>
                  </div>
                  <div style={{marginTop:8, fontSize:12, opacity:.8}}>
                    Hinweis: Standard ist SVG (skalierbar & scharf). Nutze das PNG für Plattformen ohne SVG‑Support oder als statische Vorschau. Beide URLs liefern immer die aktuelle Version – auch im Wartungsmodus.
                  </div>
                </div>
              );
            } catch {
              return <div style={{color:'#ff8484'}}>Promo‑URL konnte nicht erstellt werden.</div>;
            }
          })()}
        </div>
      </section>

      {/* Deine Statistiken */}
      <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
        <div className="highlight-card" style={{gap:18, padding:24}}>
          <h2 className="highlight-title" style={{marginBottom:8}}>Deine Statistiken</h2>
          <div style={{display:'grid', gridTemplateColumns:'1fr', gap:16}}>
            {/* YouTube */}
            {ytLinks.length > 0 && (
              <div>
                <div style={{fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8}}>
                  <span style={{display:'inline-flex', width:18, height:18, alignItems:'center', justifyContent:'center', background:'#FF0000', borderRadius:4}}>▶</span>
                  <span>YouTube</span>
                </div>
                <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                  {ytLinks.map((x, idx) => (
                    <button key={`yt-${idx}`} type="button" title={x.link?.name||x.link?.url} style={{
                      display:'inline-flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:14, border:'1px solid #ff4d4d', background:'#ff0000', color:'#fff', fontWeight:700, boxShadow:'0 2px 8px rgba(255,0,0,0.25)'
                    }} onClick={() => handleYouTubeStatsClick(x)}>
                      {x.plat?.icon && <img src={resolveImageUrl(x.plat.icon)} alt="" style={{width:18,height:18}} />}
                      <span>{x.link?.name || 'Kanal'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Twitch */}
            {twLinks.length > 0 && (
              <div>
                <div style={{fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8}}>
                  <span style={{display:'inline-flex', width:18, height:18, alignItems:'center', justifyContent:'center', background:'#9146FF', borderRadius:4}}>⌗</span>
                  <span>Twitch</span>
                </div>
                <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                  {twLinks.map((x, idx) => (
                    <button key={`tw-${idx}`} type="button" title={x.link?.name||x.link?.url} style={{
                      display:'inline-flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:14, border:'1px solid #b58aff', background:'#9146FF', color:'#fff', fontWeight:700, boxShadow:'0 2px 8px rgba(145,70,255,0.25)'
                    }} onClick={() => handleTwitchStatsClick(x)}>
                      {x.plat?.icon && <img src={resolveImageUrl(x.plat.icon)} alt="" style={{width:18,height:18}} />}
                      <span>{x.link?.name || 'Channel'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* TikTok */}
            {ttLinks.length > 0 && (
              <div>
                <div style={{fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8}}>
                  <span style={{display:'inline-flex', width:18, height:18, alignItems:'center', justifyContent:'center', background:'#010101', color:'#25F4EE', borderRadius:4}}>♪</span>
                  <span>TikTok</span>
                </div>
                <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                  {ttLinks.map((x, idx) => (
                    <button key={`tt-${idx}`} type="button" title={x.link?.name||x.link?.url} style={{
                      display:'inline-flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:14, border:'1px solid #25F4EE', background:'#0a0a0a', color:'#fff', fontWeight:700, boxShadow:'0 2px 8px rgba(37,244,238,0.2)'
                    }} onClick={() => handleTikTokStatsClick(x)}>
                      {x.plat?.icon && <img src={resolveImageUrl(x.plat.icon)} alt="" style={{width:18,height:18}} />}
                      <span>{x.link?.name || 'Profil'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Instagram */}
            {igLinks.length > 0 && (
              <div>
                <div style={{fontWeight:700, marginBottom:8, display:'flex', alignItems:'center', gap:8}}>
                  <span style={{display:'inline-flex', width:18, height:18, alignItems:'center', justifyContent:'center', background:'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)', borderRadius:4}}>◎</span>
                  <span>Instagram</span>
                </div>
                <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                  {igLinks.map((x, idx) => (
                    <button key={`ig-${idx}`} type="button" title={x.link?.name||x.link?.url} style={{
                      display:'inline-flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:14, border:'1px solid #DD2A7B', background:'linear-gradient(90deg, rgba(245,133,41,0.9), rgba(221,42,123,0.9))', color:'#fff', fontWeight:700, boxShadow:'0 2px 8px rgba(221,42,123,0.25)'
                    }} onClick={() => handleInstagramStatsClick(x)}>
                      {x.plat?.icon && <img src={resolveImageUrl(x.plat.icon)} alt="" style={{width:18,height:18}} />}
                      <span>{x.link?.name || 'Profil'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {(ytLinks.length + twLinks.length + ttLinks.length + igLinks.length === 0) && (
              <div style={{color:'#9aa3b2'}}>Keine Social-Links hinterlegt.</div>
            )}
          </div>
        </div>
      </section>

      {/* Stats result viewer: Twitch */}
      {(statsSel && statsSel.platform === 'twitch') && (
        <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
          <div className="highlight-card" style={{padding:24}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:10}}>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <span style={{display:'inline-flex', width:22, height:22, alignItems:'center', justifyContent:'center', background:'#9146FF', borderRadius:6}}>⌗</span>
                <div>
                  <div style={{fontWeight:800}}>Twitch · {statsSel.label} <span style={{opacity:.8, fontWeight:500}}>@{statsSel.login}</span></div>
                  <div style={{fontSize:12, opacity:.75}}>
                    {statsData?.stream ? 'Live · Stream & Kanalübersicht' : 'Kanalübersicht • VoDs und Clips'}
                  </div>
                </div>
              </div>
              {/* Tabs */}
              <div style={{display:'inline-flex', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:999, overflow:'hidden'}}>
                {['overview','vods','clips','followers','analytics'].map(t => (
                  <button key={t} onClick={() => onChangeTab(t)} style={{
                    padding:'8px 14px', fontWeight:700, fontSize:13, color:'#fff', background: statsTab===t ? '#9146FF' : 'transparent', border:'none', cursor:'pointer'
                  }}>{t === 'overview' ? 'Übersicht' : (t === 'vods' ? 'VoDs' : (t === 'clips' ? 'Clips' : (t === 'followers' ? 'Follower' : 'Analytics')))}</button>
                ))}
              </div>
            </div>
            {statsLoading && (
              <div style={{padding:16, textAlign:'center', color:'#9aa3b2'}}>Twitch-Daten werden geladen …</div>
            )}
            {!statsLoading && statsError && (
              <div style={{padding:16, textAlign:'center', color:'#ff8484'}}>{statsError}</div>
            )}
            {!statsLoading && !statsError && statsData && (
              <div>
                {statsTab === 'overview' && (
                  <div>
                    {/* Channel header */}
                    {(statsData.user || statsData.stream) && (
                      <div style={{display:'grid', gridTemplateColumns:'1fr', gap:12, marginBottom:12}}>
                        <div style={{display:'flex', gap:12, alignItems:'center'}}>
                          {statsData.user?.profile_image_url && (
                            <img src={resolveImageUrl(statsData.user.profile_image_url)} alt="Avatar" style={{width:48, height:48, borderRadius:'50%', objectFit:'cover', border:'2px solid rgba(145,70,255,0.6)'}} />
                          )}
                          <div style={{minWidth:0}}>
                            <div style={{fontWeight:800, fontSize:16}}>{statsData.user?.display_name || statsSel.label} <span style={{opacity:.8, fontWeight:500}}>@{statsSel.login}</span></div>
                            {statsData.user?.description && (
                              <div style={{fontSize:12, opacity:.85, whiteSpace:'pre-wrap', lineHeight:'1.35'}}>{statsData.user.description}</div>
                            )}
                          </div>
                        </div>
                        {statsData.stream && (
                          <div style={{display:'flex', gap:12, alignItems:'center', background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.3)', borderRadius:10, padding:10}}>
                            <span style={{background:'#e91916', color:'#fff', fontWeight:900, fontSize:12, padding:'3px 8px', borderRadius:999}}>LIVE</span>
                            <div style={{minWidth:0}}>
                              <div style={{fontWeight:700, marginBottom:2}}>{statsData.stream.title || 'Live-Stream'}</div>
                              <div style={{fontSize:12, opacity:.85}}>
                                {typeof statsData.stream.viewer_count === 'number' && <span style={{marginRight:10}}>{statsData.stream.viewer_count} Zuschauer</span>}
                                {statsData.game?.name && <span>Spiel: {statsData.game.name}</span>}
                              </div>
                            </div>
                            {statsData.game?.box_art_url && (
                              <img src={resolveImageUrl(statsData.game.box_art_url)} alt={statsData.game.name||'Game'} style={{width:56, height:74, borderRadius:8, marginLeft:'auto', objectFit:'cover'}} />
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
                      <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                        <div style={{opacity:.8, fontSize:13}}>VoDs (neueste)</div>
                        <div style={{fontWeight:800, fontSize:22}}>{statsData.vods?.length || 0}</div>
                      </div>
                      <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                        <div style={{opacity:.8, fontSize:13}}>Clips (neueste)</div>
                        <div style={{fontWeight:800, fontSize:22}}>{statsData.clips?.length || 0}</div>
                      </div>
                    </div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12}}>
                      {[...(statsData.vods||[]).slice(0,3), ...(statsData.clips||[]).slice(0,3)].map((item, i) => (
                        <a key={`ov-${i}`} href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                          <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56', transform:'translateZ(0)' }}>
                            {item.thumbnailUrl && <img src={resolveImageUrl(item.thumbnailUrl)} alt={item.title||''} style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />}
                          </div>
                          <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{item.title || 'Twitch'}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {statsTab === 'vods' && (
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12}}>
                    {(statsData.vods||[]).map(v => (
                      <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                        <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56' }}>
                          {v.thumbnailUrl && <img src={resolveImageUrl(v.thumbnailUrl)} alt={v.title||''} style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} />}
                          {v.duration && <span style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,0.55)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>{v.duration}</span>}
                        </div>
                        <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                      </a>
                    ))}
                    {(statsData.vods||[]).length === 0 && <div style={{opacity:.8}}>Keine VoDs gefunden.</div>}
                  </div>
                )}
                {statsTab === 'clips' && (
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12}}>
                    {(statsData.clips||[]).map(c => (
                      <a key={c.id} href={c.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                        <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#0f2a56' }}>
                          {c.thumbnailUrl && <img src={resolveImageUrl(c.thumbnailUrl)} alt={c.title||''} style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} />}
                          {c.duration && <span style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,0.55)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>{c.duration}s</span>}
                        </div>
                        <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.title}</div>
                      </a>
                    ))}
                    {(statsData.clips||[]).length === 0 && <div style={{opacity:.8}}>Keine Clips gefunden.</div>}
                  </div>
                )}
                {statsTab === 'followers' && (
                  <div>
                    {(twitchConn.connected === true && statsSel?.login && twitchConn.login && String(twitchConn.login).toLowerCase() === String(statsSel.login).toLowerCase()) && (
                      <div style={{padding:8, marginBottom:8, background:'rgba(50,205,137,0.12)', border:'1px solid rgba(50,205,137,0.35)', borderRadius:8, fontSize:12}}>
                        Quelle: Broadcaster‑Token (genauer Zähler, Berechtigung: moderator:read:followers)
                      </div>
                    )}
                    {(twitchConn.connected === false) && (
                      <div style={{padding:8, marginBottom:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, fontSize:12}}>
                        Quelle: App‑Token per Login (eingeschränkte Sicht). Verbinde deinen Kanal unter „Kanal‑Verknüpfungen“ für genauere Daten.
                      </div>
                    )}
                    {followersLoading && <div style={{padding:12, color:'#9aa3b2'}}>Follower werden geladen …</div>}
                    {!followersLoading && followersError && (
                      <div style={{padding:12}}>
                        <div style={{ color:'#ff8484' }}>{followersError}</div>
                        <div style={{ marginTop:8, fontSize:12, opacity:.8 }}>
                          Tipp: Prüfe auf der Seite “Kanal‑Verknüpfungen”, ob Twitch verbunden ist und der richtige Channel gewählt wurde.
                        </div>
                      </div>
                    )}
                    {!followersLoading && !followersError && statsData.followers && (
                      <div>
                        <div style={{display:'flex', flexWrap:'wrap', gap:12, marginBottom:12}}>
                          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                            <div style={{opacity:.8, fontSize:13}}>Follower gesamt</div>
                            <div style={{fontWeight:800, fontSize:22}}>{statsData.followers.total || 0}</div>
                          </div>
                          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                            <div style={{opacity:.8, fontSize:13}}>Neue Follower · 7 Tage</div>
                            <div style={{fontWeight:800, fontSize:22}}>{statsData.followers.recent?.last7 || 0}</div>
                          </div>
                          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                            <div style={{opacity:.8, fontSize:13}}>Neue Follower · 30 Tage</div>
                            <div style={{fontWeight:800, fontSize:22}}>{statsData.followers.recent?.last30 || 0}</div>
                          </div>
                        </div>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                          <div style={{fontWeight:700}}>Follower-Liste</div>
                          <div style={{display:'flex', gap:8}}>
                            <button disabled={followersPrevStack.length === 0} onClick={() => {
                              if (followersPrevStack.length === 0) return;
                              const prev = [...followersPrevStack];
                              const last = prev.pop();
                              setFollowersPrevStack(prev);
                              fetchTwitchFollowers(last);
                            }} className="hero-btn-secondary" style={{padding:'6px 10px'}}>Zurück</button>
                            <button disabled={!statsData.followers?.pagination?.cursor} onClick={() => {
                              const cur = statsData.followers?.pagination?.cursor;
                              if (cur) {
                                setFollowersPrevStack(prev => [...prev, cur]);
                                fetchTwitchFollowers(cur);
                              }
                            }} className="hero-btn-secondary" style={{padding:'6px 10px'}}>Weiter</button>
                          </div>
                        </div>
                        {/* Simple 30-day follower histogram */}
                        {Array.isArray(statsData.followers.histogram) && statsData.followers.histogram.length > 0 && (
                          <div style={{margin:'8px 0 12px'}}>
                            <div style={{fontWeight:700, marginBottom:6}}>Follower-Zuwachs · 30 Tage</div>
                            {(() => {
                              const hist = statsData.followers.histogram;
                              const w = 360, h = 72, pad = 6;
                              const max = Math.max(1, ...hist.map(d => d.count||0));
                              const step = (w - pad*2) / (hist.length - 1);
                              const points = hist.map((d, i) => {
                                const x = pad + i*step;
                                const y = h - pad - ((d.count||0) / max) * (h - pad*2);
                                return `${Math.round(x)},${Math.round(y)}`;
                              }).join(' ');
                              return (
                                <svg width={w} height={h} style={{display:'block', width:'100%', maxWidth:420}}>
                                  <rect x="0" y="0" width={w} height={h} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)"/>
                                  <polyline fill="none" stroke="#32CD89" strokeWidth="2" points={points} />
                                </svg>
                              );
                            })()}
                          </div>
                        )}
                        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:10}}>
                          {(statsData.followers.items || []).map((f, i) => (
                            <div key={(f.from_id||f.user_id||'f') + '-' + i} style={{border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:10}}>
                              <div style={{fontWeight:700}}>{f.from_name || f.user_name || f.from_login || f.user_login}</div>
                              <div style={{fontSize:12, opacity:.75}}>Gefolgt am {f.followed_at ? new Date(f.followed_at).toLocaleString() : '—'}</div>
                            </div>
                          ))}
                          {(statsData.followers.items || []).length === 0 && <div style={{opacity:.8}}>Keine Follower gefunden.</div>}
                        </div>
                        <div style={{ marginTop:8 }}>
                          <button className="hero-btn-secondary" style={{ padding:'6px 10px' }} onClick={() => setFollowersDebugOpen(v => !v)}>
                            {followersDebugOpen ? 'Debug verbergen' : 'Debug anzeigen'}
                          </button>
                        </div>
                        {followersDebugOpen && (
                          <pre style={{ marginTop:8, background:'#0b1020', color:'#e5e7eb', padding:10, borderRadius:8, maxWidth:'100%', overflow:'auto' }}>{JSON.stringify(statsData.followers, null, 2)}</pre>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {statsTab === 'analytics' && (
                  <div>
                    {(() => {
                      const vods = statsData.vods || [];
                      const totalViews = vods.reduce((sum, v) => sum + (Number(v.viewCount)||0), 0);
                      const avgViews = vods.length ? Math.round(totalViews / vods.length) : 0;
                      const parseDur = (s) => {
                        if (!s || typeof s !== 'string') return 0;
                        let m = s.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
                        if (!m) return 0; const h = parseInt(m[1]||'0'); const mm = parseInt(m[2]||'0'); const ss = parseInt(m[3]||'0');
                        return h*3600 + mm*60 + ss;
                      };
                      const durs = vods.map(v => parseDur(v.duration||''));
                      const avgDur = durs.length ? Math.round(durs.reduce((a,b)=>a+b,0) / durs.length) : 0;
                      const fmt = (sec) => {
                        const h = Math.floor(sec/3600), m = Math.floor((sec%3600)/60), s = sec%60;
                        return (h? (h+'h'):'') + (m? (m+'m'):'') + (s? (s+'s'): (!h && !m ? '0s' : ''));
                      };
                      return (
                        <div>
                          <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
                            <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Durchschn. VoD‑Views</div>
                              <div style={{fontWeight:800, fontSize:22}}>{avgViews}</div>
                            </div>
                            <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Summe VoD‑Views</div>
                              <div style={{fontWeight:800, fontSize:22}}>{totalViews}</div>
                            </div>
                            <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(145,70,255,0.15)', border:'1px solid rgba(145,70,255,0.35)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Durchschn. Streamdauer</div>
                              <div style={{fontWeight:800, fontSize:22}}>{fmt(avgDur)}</div>
                            </div>
                            {/* Twitch gated metrics (using broadcaster token) */}
                            <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(50,205,137,0.12)', border:'1px solid rgba(50,205,137,0.35)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Aktive Abos (Twitch)</div>
                              <div style={{fontWeight:800, fontSize:22}}>
                                {twSubsLoading ? '…' : (statsData?.subs?.total ?? '–')}
                              </div>
                              {twSubsError && <div style={{fontSize:12, color:'#ff8484'}}>{twSubsError}</div>}
                            </div>
                            <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(50,205,137,0.12)', border:'1px solid rgba(50,205,137,0.35)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Aktuelle Chatter (Twitch)</div>
                              <div style={{fontWeight:800, fontSize:22}}>
                                {twChattersLoading ? '…' : (statsData?.chatters?.total ?? '–')}
                              </div>
                              {twChattersError && <div style={{fontSize:12, color:'#ff8484'}}>{twChattersError}</div>}
                            </div>
                            {/* Hype Train bits/subs snapshot */}
                            <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Hype Train (letztes Ereignis)</div>
                              {twHype ? (
                                <div>
                                  <div style={{fontWeight:800, fontSize:22}}>{twHype.summary?.lastEventBits || 0} Bits</div>
                                  <div style={{opacity:.85, fontSize:12}}>Subs‑Beiträge: {twHype.summary?.lastEventSubs || 0}</div>
                                  {twHype.estimates && (
                                    <div style={{opacity:.85, fontSize:12}}>Bits‑Einnahmen (≈): {typeof twHype.estimates.bitsEur==='number' ? `${twHype.estimates.bitsEur.toFixed(2)} €` : '–'}</div>
                                  )}
                                </div>
                              ) : (
                                <div style={{fontSize:13}}>{twHypeError || '…'}</div>
                              )}
                              <div style={{fontSize:11, opacity:.7, marginTop:6}}>Hinweis: Hype‑Train deckt nur Events ab und ist keine vollständige Bits‑Historie.</div>
                            </div>
                            {/* Estimated revenue */}
                            <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(255,215,0,0.10)', border:'1px solid rgba(255,215,0,0.35)', borderRadius:12, padding:12}}>
                              <div style={{opacity:.8, fontSize:13}}>Geschätzte Monats‑Einnahmen</div>
                              <div style={{fontWeight:800, fontSize:22}}>
                                {twRevenue ? `${twRevenue.estMonthly?.toFixed ? twRevenue.estMonthly.toFixed(2) : twRevenue.estMonthly} €` : (twRevenueError ? '–' : '…')}
                              </div>
                              {twRevenue && (
                                <div style={{fontSize:12, opacity:.85, marginTop:4}}>
                                  <span>Tier1: {twRevenue.breakdown?.tier1||0} · Tier2: {twRevenue.breakdown?.tier2||0} · Tier3: {twRevenue.breakdown?.tier3||0} · Gifted: {twRevenue.gifted||0}</span>
                                </div>
                              )}
                              {twRevenueError && <div style={{fontSize:12, color:'#ff8484'}}>{twRevenueError}</div>}
                              {twRevenue?.disclaimer && <div style={{fontSize:11, opacity:.7, marginTop:6}}>{twRevenue.disclaimer}</div>}
                            </div>
                          </div>
                          <div style={{fontWeight:700, marginBottom:6}}>Letzte Streamdauern (aus VoDs)</div>
                          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                            {vods.slice(0,6).map(v => (
                              <span key={v.id} style={{background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'4px 8px', fontSize:12}}>{fmt(parseDur(v.duration||''))}</span>
                            ))}
                            {vods.length === 0 && <span style={{opacity:.8}}>Keine VoDs verfügbar.</span>}
                          </div>
                          {/* Tiny sparkline for recent VoD views */}
                          {vods.length > 0 && (
                            <div style={{marginTop:12}}>
                              <div style={{fontWeight:700, marginBottom:6}}>VoD‑Views Verlauf (letzte {Math.min(10, vods.length)} VoDs)</div>
                              {(() => {
                                const arr = vods.slice(0, Math.min(10, vods.length)).map(v => Number(v.viewCount)||0).reverse();
                                const w = 360, h = 72, pad = 6;
                                const max = Math.max(1, ...arr);
                                const step = (w - pad*2) / (arr.length - 1 || 1);
                                const points = arr.map((val, i) => {
                                  const x = pad + i*step;
                                  const y = h - pad - (val / max) * (h - pad*2);
                                  return `${Math.round(x)},${Math.round(y)}`;
                                }).join(' ');
                                return (
                                  <svg width={w} height={h} style={{display:'block', width:'100%', maxWidth:420}}>
                                    <rect x="0" y="0" width={w} height={h} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)"/>
                                    <polyline fill="none" stroke="#9146FF" strokeWidth="2" points={points} />
                                  </svg>
                                );
                              })()}
                            </div>
                          )}
                          <div style={{marginTop:10, fontSize:12, opacity:.75}}>Hinweis: Genaue Live‑Metriken (Spitzenzuschauer, eindeutige Chatter, Einnahmen) erfordern zusätzliche Twitch‑Berechtigungen. Siehe Hinweis unten.</div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Stats result viewer: YouTube */}
      {(statsSel && statsSel.platform === 'youtube') && (
        <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
          <div className="highlight-card" style={{padding:24}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:10}}>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <span style={{display:'inline-flex', width:22, height:22, alignItems:'center', justifyContent:'center', background:'#FF0000', borderRadius:6}}>▶</span>
                <div>
                  <div style={{fontWeight:800}}>YouTube · {statsSel.label}</div>
                  <div style={{fontSize:12, opacity:.75}}>Kanalübersicht • Videos • Analytics</div>
                </div>
              </div>
              <div style={{display:'inline-flex', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:999, overflow:'hidden'}}>
                {['overview','videos','analytics'].map(t => (
                  <button key={t} onClick={() => setStatsTab(t)} style={{
                    padding:'8px 14px', fontWeight:700, fontSize:13, color:'#fff', background: statsTab===t ? '#FF0000' : 'transparent', border:'none', cursor:'pointer'
                  }}>{t === 'overview' ? 'Übersicht' : (t === 'videos' ? 'Videos' : 'Analytics')}</button>
                ))}
              </div>
            </div>
            {statsLoading && (
              <div style={{padding:16, textAlign:'center', color:'#9aa3b2'}}>YouTube‑Daten werden geladen …</div>
            )}
            {!statsLoading && statsError && (
              <div style={{padding:16, textAlign:'center', color:'#ff8484'}}>{statsError}</div>
            )}
            {!statsLoading && !statsError && statsData && (
              <div>
                {statsTab === 'overview' && (
                  <div>
                    <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
                      <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
                        <div style={{opacity:.8, fontSize:13}}>Abonnenten</div>
                        <div style={{fontWeight:800, fontSize:22}}>{statsData.channelStats?.subscribers ?? '–'}</div>
                      </div>
                      <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
                        <div style={{opacity:.8, fontSize:13}}>Gesamt‑Views</div>
                        <div style={{fontWeight:800, fontSize:22}}>{statsData.channelStats?.views ?? '–'}</div>
                      </div>
                      <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
                        <div style={{opacity:.8, fontSize:13}}>Videos gesamt</div>
                        <div style={{fontWeight:800, fontSize:22}}>{statsData.channelStats?.videoCount ?? '–'}</div>
                      </div>
                    </div>
                    <div style={{fontWeight:700, marginBottom:6}}>Neueste Videos</div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12}}>
                      {(statsData.recent||[]).slice(0,6).map(v => (
                        <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                          <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#290b0b' }}>
                            {v.thumbnailUrl && <img src={resolveImageUrl(v.thumbnailUrl)} alt={v.title||''} style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />}
                          </div>
                          <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title || 'YouTube'}</div>
                          <div style={{ fontSize:12, opacity:.8 }}>{v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : ''} · {typeof v.viewCount==='number' ? `${v.viewCount} Views` : ''}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {statsTab === 'videos' && (
                  <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12}}>
                    {(statsData.recent||[]).map(v => (
                      <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                        <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#290b0b' }}>
                          {v.thumbnailUrl && <img src={resolveImageUrl(v.thumbnailUrl)} alt={v.title||''} style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} />}
                        </div>
                        <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                        <div style={{ fontSize:12, opacity:.8 }}>{v.publishedAt ? new Date(v.publishedAt).toLocaleDateString() : ''} · {typeof v.viewCount==='number' ? `${v.viewCount} Views` : ''}</div>
                      </a>
                    ))}
                    {(statsData.recent||[]).length === 0 && <div style={{opacity:.8}}>Keine Videos gefunden.</div>}
                  </div>
                )}
                {statsTab === 'analytics' && (
                  <div>
                    {/* If connected via OAuth, fetch and display real analytics; else show basic sparkline with CTA */}
                    {youtubeConn.connected ? (
                      <YouTubeAnalyticsPanel ytAnalytics={ytAnalytics} ytAnalyticsError={ytAnalyticsError} onLoad={async () => {
                        try {
                          setYtAnalyticsError('');
                          setYtAnalytics(null);
                          const { data } = await api.get('/social-content/me/youtube/analytics/overview', { params: { days: 30 } });
                          setYtAnalytics(data);
                        } catch (e) {
                          setYtAnalyticsError('YouTube Analytics konnten nicht geladen werden.');
                        }
                      }} />
                    ) : (
                      (() => {
                        const vids = statsData.recent || [];
                        const views = vids.slice(0, Math.min(12, vids.length)).map(v => Number(v.viewCount)||0).reverse();
                        const w = 360, h = 72, pad = 6;
                        const max = Math.max(1, ...views);
                        const step = (w - pad*2) / (Math.max(1, views.length - 1));
                        const points = views.map((val, i) => {
                          const x = pad + i*step;
                          const y = h - pad - (val / max) * (h - pad*2);
                          return `${Math.round(x)},${Math.round(y)}`;
                        }).join(' ');
                        const total = views.reduce((a,b)=>a+b,0);
                        const avg = views.length ? Math.round(total / views.length) : 0;
                        return (
                          <div>
                            <div style={{padding:8, marginBottom:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, fontSize:12}}>
                              Für echte YouTube‑Analytics (Views, Watch Time, Abos) bitte deinen Kanal unter „Kanal‑Verknüpfungen“ verbinden.
                            </div>
                            <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
                              <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
                                <div style={{opacity:.8, fontSize:13}}>Durchschn. Views (letzte {views.length})</div>
                                <div style={{fontWeight:800, fontSize:22}}>{avg}</div>
                              </div>
                              <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
                                <div style={{opacity:.8, fontSize:13}}>Summe Views</div>
                                <div style={{fontWeight:800, fontSize:22}}>{total}</div>
                              </div>
                            </div>
                            <div style={{fontWeight:700, marginBottom:6}}>Views‑Verlauf (letzte {views.length} Videos)</div>
                            <svg width={w} height={h} style={{display:'block', width:'100%', maxWidth:420}}>
                              <rect x="0" y="0" width={w} height={h} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)"/>
                              <polyline fill="none" stroke="#FF0000" strokeWidth="2" points={points} />
                            </svg>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Stats result viewer: TikTok */}
      {(statsSel && statsSel.platform === 'tiktok') && (
        <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
          <div className="highlight-card" style={{padding:24}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:10}}>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <span style={{display:'inline-flex', width:22, height:22, alignItems:'center', justifyContent:'center', background:'#010101', color:'#25F4EE', borderRadius:6}}>♪</span>
                <div>
                  <div style={{fontWeight:800}}>TikTok · {statsSel.label}</div>
                  <div style={{fontSize:12, opacity:.75}}>Profilübersicht • Videos • Analytics</div>
                </div>
              </div>
              <div style={{display:'inline-flex', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:999, overflow:'hidden'}}>
                {['overview','videos','analytics'].map(t => (
                  <button key={t} onClick={() => onChangeTab(t)} style={{
                    padding:'8px 14px', fontWeight:700, fontSize:13, color:'#fff', background: statsTab===t ? '#25F4EE' : 'transparent', border:'none', cursor:'pointer'
                  }}>{t === 'overview' ? 'Übersicht' : (t === 'videos' ? 'Videos' : 'Analytics')}</button>
                ))}
              </div>
            </div>
            {statsLoading && (
              <div style={{padding:16, textAlign:'center', color:'#9aa3b2'}}>TikTok‑Daten werden geladen …</div>
            )}
            {!statsLoading && statsError && (
              <div style={{padding:16, textAlign:'center', color:'#ff8484'}}>{statsError}</div>
            )}
            {!statsLoading && !statsError && statsData && (
              <div>
                {statsTab === 'overview' && (
                  <div>
                    <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
                      <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(37,244,238,0.12)', border:'1px solid rgba(37,244,238,0.35)', borderRadius:12, padding:12}}>
                        <div style={{opacity:.8, fontSize:13}}>Videos (neueste)</div>
                        <div style={{fontWeight:800, fontSize:22}}>{statsData.videos?.length || 0}</div>
                      </div>
                    </div>
                    <div style={{fontWeight:700, marginBottom:6}}>Neueste Videos</div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12}}>
                      {(statsData.videos||[]).slice(0,6).map(v => (
                        <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                          <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#060a0a' }}>
                            {v.thumbnailUrl && <img src={resolveImageUrl(v.thumbnailUrl)} alt={v.title||''} style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />}
                          </div>
                          <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title || 'TikTok'}</div>
                          <div style={{ fontSize:12, opacity:.8 }}>{v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ''}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {statsTab === 'videos' && (
                  <div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12}}>
                      {(statsData.videos||[]).map(v => (
                      <a key={v.id} href={v.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                        <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#060a0a' }}>
                          {v.thumbnailUrl && <img src={resolveImageUrl(v.thumbnailUrl)} alt={v.title||''} style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} />}
                        </div>
                        <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.title}</div>
                        <div style={{ fontSize:12, opacity:.8 }}>{v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ''}</div>
                      </a>
                      ))}
                      {(statsData.videos||[]).length === 0 && <div style={{opacity:.8}}>Keine Videos gefunden.</div>}
                    </div>
                    {statsData?.pagination?.has_more && (
                      <div style={{marginTop:12, textAlign:'center'}}>
                        <button onClick={loadMoreTikTokVideos} className="hero-btn-secondary" style={{padding:'8px 14px'}} disabled={ttMoreLoading}>
                          {ttMoreLoading ? 'Lädt …' : 'Mehr laden'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {statsTab === 'analytics' && (
                  <div>
                    {tiktokConn.connected !== true && (
                      <div style={{padding:8, marginBottom:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, fontSize:12}}>
                        Verbinde dein TikTok unter <Link to="/integrations" style={{color:'#25F4EE'}}>Kanal‑Verknüpfungen</Link>, um Analytics zu sehen.
                      </div>
                    )}
                    {tiktokConn.connected === true && (
                      <TikTokAnalyticsPanel ttAnalytics={ttAnalytics} ttAnalyticsError={ttAnalyticsError} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Stats result viewer: Instagram */}
      {(statsSel && statsSel.platform === 'instagram') && (
        <section style={{width:'100%',maxWidth:'1200px',margin:'18px auto 0',padding:0}}>
          <div className="highlight-card" style={{padding:24}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:10}}>
              <div style={{display:'flex', alignItems:'center', gap:10}}>
                <span style={{display:'inline-flex', width:22, height:22, alignItems:'center', justifyContent:'center', background:'linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4)', borderRadius:6}}>◎</span>
                <div>
                  <div style={{fontWeight:800}}>Instagram · {statsSel.label}</div>
                  <div style={{fontSize:12, opacity:.75}}>Profil • Medien • Analytics</div>
                </div>
              </div>
              <div style={{display:'inline-flex', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:999, overflow:'hidden'}}>
                {['overview','media','analytics'].map(t => (
                  <button key={t} onClick={() => onChangeTab(t)} style={{
                    padding:'8px 14px', fontWeight:700, fontSize:13, color:'#fff', background: statsTab===t ? '#DD2A7B' : 'transparent', border:'none', cursor:'pointer'
                  }}>{t === 'overview' ? 'Übersicht' : (t === 'media' ? 'Medien' : 'Analytics')}</button>
                ))}
              </div>
            </div>
            {statsLoading && (
              <div style={{padding:16, textAlign:'center', color:'#9aa3b2'}}>Instagram‑Daten werden geladen …</div>
            )}
            {!statsLoading && statsError && (
              <div style={{padding:16, textAlign:'center', color:'#ff8484'}}>{statsError}</div>
            )}
            {!statsLoading && !statsError && statsData && (
              <div>
                {statsTab === 'overview' && (
                  <div>
                    {(statsData.profile) && (
                      <div style={{display:'flex', gap:12, alignItems:'center', marginBottom:12}}>
                        {statsData.profile.profile_picture_url && (
                          <img src={resolveImageUrl(statsData.profile.profile_picture_url)} alt="Avatar" style={{width:48, height:48, borderRadius:'50%', objectFit:'cover', border:'2px solid rgba(221,42,123,0.6)'}} />
                        )}
                        <div style={{minWidth:0}}>
                          <div style={{fontWeight:800, fontSize:16}}>@{statsData.profile.username || 'instagram'}</div>
                          {statsData.profile.biography && (
                            <div style={{fontSize:12, opacity:.85, whiteSpace:'pre-wrap', lineHeight:'1.35'}}>{statsData.profile.biography}</div>
                          )}
                        </div>
                      </div>
                    )}
                    <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
                      {typeof statsData.profile?.followers_count === 'number' && (
                        <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(221,42,123,0.12)', border:'1px solid rgba(221,42,123,0.35)', borderRadius:12, padding:12}}>
                          <div style={{opacity:.8, fontSize:13}}>Follower</div>
                          <div style={{fontWeight:800, fontSize:22}}>{statsData.profile.followers_count}</div>
                        </div>
                      )}
                      {typeof statsData.profile?.follows_count === 'number' && (
                        <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(221,42,123,0.12)', border:'1px solid rgba(221,42,123,0.35)', borderRadius:12, padding:12}}>
                          <div style={{opacity:.8, fontSize:13}}>Abonniert</div>
                          <div style={{fontWeight:800, fontSize:22}}>{statsData.profile.follows_count}</div>
                        </div>
                      )}
                      {typeof statsData.profile?.media_count === 'number' && (
                        <div style={{flex:'1 1 240px', minWidth:220, background:'rgba(221,42,123,0.12)', border:'1px solid rgba(221,42,123,0.35)', borderRadius:12, padding:12}}>
                          <div style={{opacity:.8, fontSize:13}}>Medien gesamt</div>
                          <div style={{fontWeight:800, fontSize:22}}>{statsData.profile.media_count}</div>
                        </div>
                      )}
                    </div>
                    <div style={{fontWeight:700, marginBottom:6}}>Neueste Medien</div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:12}}>
                      {(statsData.media||[]).slice(0,6).map(m => (
                        <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                          <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#2f0f25' }}>
                            {m.thumbnailUrl && <img src={resolveImageUrl(m.thumbnailUrl)} alt={m.title||''} style={{ width:'100%', height:120, objectFit:'cover', display:'block' }} />}
                          </div>
                          <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.title || 'Instagram'}</div>
                          <div style={{ fontSize:12, opacity:.8 }}>{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : ''}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {statsTab === 'media' && (
                  <div>
                    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12}}>
                      {(statsData.media||[]).map(m => (
                        <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration:'none', color:'#fff' }}>
                          <div style={{ position:'relative', borderRadius:8, overflow:'hidden', background:'#2f0f25' }}>
                            {m.thumbnailUrl && <img src={resolveImageUrl(m.thumbnailUrl)} alt={m.title||''} style={{ width:'100%', height:140, objectFit:'cover', display:'block' }} />}
                            {(typeof m?.stats?.likes==='number' || typeof m?.stats?.comments==='number') && (
                              <div style={{ position:'absolute', bottom:6, right:6, background:'rgba(0,0,0,0.55)', padding:'2px 6px', borderRadius:6, fontSize:12 }}>
                                ❤ {m?.stats?.likes ?? 0} · 💬 {m?.stats?.comments ?? 0}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize:14, fontWeight:600, marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{m.title}</div>
                          <div style={{ fontSize:12, opacity:.8 }}>{m.createdAt ? new Date(m.createdAt).toLocaleDateString() : ''}</div>
                        </a>
                      ))}
                      {(statsData.media||[]).length === 0 && <div style={{opacity:.8}}>Keine Medien gefunden.</div>}
                    </div>
                    {statsData?.pagination?.has_more && (
                      <div style={{marginTop:12, textAlign:'center'}}>
                        <button onClick={loadMoreInstagramMedia} className="hero-btn-secondary" style={{padding:'8px 14px'}} disabled={igMoreLoading}>
                          {igMoreLoading ? 'Lädt …' : 'Mehr laden'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {statsTab === 'analytics' && (
                  <div>
                    {instagramConn.connected !== true && (
                      <div style={{padding:8, marginBottom:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, fontSize:12}}>
                        Verbinde dein Instagram unter <Link to="/integrations" style={{color:'#DD2A7B'}}>Kanal‑Verknüpfungen</Link>, um Analytics zu sehen.
                      </div>
                    )}
                    {instagramConn.connected === true && (
                      <InstagramAnalyticsPanel igAnalytics={igAnalytics} igAnalyticsError={igAnalyticsError} onLoad={() => {
                        if (!igAnalytics) fetchInstagramAnalytics();
                      }} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function YouTubeAnalyticsPanel({ ytAnalytics, ytAnalyticsError, onLoad }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      onLoad?.();
    }
  }, [loaded]);
  if (ytAnalyticsError) return <div style={{padding:12}}>
    <div style={{color:'#ff8484'}}>{ytAnalyticsError}</div>
    <div style={{fontSize:12, opacity:.8, marginTop:6}}>Tipp: Stelle sicher, dass sowohl “YouTube Data API v3” als auch “YouTube Analytics API” in der Google Cloud Console für das Projekt aktiviert sind. Im OAuth‑Consent ggf. Testnutzer hinzufügen.</div>
  </div>;
  if (!ytAnalytics) return <div style={{padding:12, color:'#9aa3b2'}}>Analytics werden geladen …</div>;
  const s = ytAnalytics.summary || { views: 0, minutes: 0, subsGained: 0, subsLost: 0, netSubs: 0 };
  const series = Array.isArray(ytAnalytics.series) ? ytAnalytics.series : [];
  const views = series.map(d => d.views);
  const w = 360, h = 72, pad = 6;
  const max = Math.max(1, ...views);
  const step = (w - pad*2) / (Math.max(1, views.length - 1));
  const points = views.map((val, i) => {
    const x = pad + i*step;
    const y = h - pad - (val / max) * (h - pad*2);
    return `${Math.round(x)},${Math.round(y)}`;
  }).join(' ');
  return (
    <div>
      <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Summe Views</div>
          <div style={{fontWeight:800, fontSize:22}}>{s.views}</div>
        </div>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Watch Time (Minuten)</div>
          <div style={{fontWeight:800, fontSize:22}}>{s.minutes}</div>
        </div>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,0,0,0.12)', border:'1px solid rgba(255,0,0,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Abos + / −</div>
          <div style={{fontWeight:800, fontSize:22}}>{s.subsGained} / {s.subsLost} (Netto {s.netSubs})</div>
        </div>
      </div>
      <div style={{fontWeight:700, marginBottom:6}}>Views Verlauf (täglich)</div>
      <svg width={w} height={h} style={{display:'block', width:'100%', maxWidth:420}}>
        <rect x="0" y="0" width={w} height={h} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)"/>
        <polyline fill="none" stroke="#FF0000" strokeWidth="2" points={points} />
      </svg>
      <div style={{fontSize:11, opacity:.75, marginTop:6}}>Datenquelle: YouTube Analytics API (OAuth).</div>
    </div>
  );
}

function TikTokAnalyticsPanel({ ttAnalytics, ttAnalyticsError }) {
  if (ttAnalyticsError) return <div style={{padding:12}}>
    <div style={{color:'#ff8484'}}>{ttAnalyticsError}</div>
    <div style={{fontSize:12, opacity:.8, marginTop:6}}>Tipp: Stelle sicher, dass TikTok OAuth verbunden ist und ausreichende Berechtigungen (user.info.basic, user.info.profile, video.list) vorliegen.</div>
  </div>;
  if (!ttAnalytics) return <div style={{padding:12, color:'#9aa3b2'}}>Analytics werden geladen …</div>;
  const totals = ttAnalytics.totals || { views: 0, likes: 0, comments: 0, shares: 0 };
  const profile = ttAnalytics.profile || {};
  const hasAny = (totals.views||0) + (totals.likes||0) + (totals.comments||0) + (totals.shares||0) > 0;
  return (
    <div>
      <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(37,244,238,0.12)', border:'1px solid rgba(37,244,238,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Summe Views</div>
          <div style={{fontWeight:800, fontSize:22}}>{totals.views}</div>
        </div>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(37,244,238,0.12)', border:'1px solid rgba(37,244,238,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Likes</div>
          <div style={{fontWeight:800, fontSize:22}}>{totals.likes}</div>
        </div>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(37,244,238,0.12)', border:'1px solid rgba(37,244,238,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Kommentare</div>
          <div style={{fontWeight:800, fontSize:22}}>{totals.comments}</div>
        </div>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(37,244,238,0.12)', border:'1px solid rgba(37,244,238,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Shares</div>
          <div style={{fontWeight:800, fontSize:22}}>{totals.shares}</div>
        </div>
      </div>
      {!hasAny && (ttAnalytics.count || 0) > 0 && (
        <div style={{padding:8, marginTop:8, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, fontSize:12}}>
          Hinweis: Es wurden Videos gefunden, aber keine Statistikwerte geliefert. Bitte verbinde TikTok ggf. erneut und erlaube zusätzliche Berechtigungen (user.info.stats, video.list).
        </div>
      )}
      <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>
        {typeof profile.follower_count === 'number' && (
          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:12}}>
            <div style={{opacity:.8, fontSize:13}}>Follower</div>
            <div style={{fontWeight:800, fontSize:22}}>{profile.follower_count}</div>
          </div>
        )}
        {typeof profile.video_count === 'number' && (
          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:12}}>
            <div style={{opacity:.8, fontSize:13}}>Videos gesamt</div>
            <div style={{fontWeight:800, fontSize:22}}>{profile.video_count}</div>
          </div>
        )}
      </div>
      <div style={{fontSize:11, opacity:.75, marginTop:6}}>Datenquelle: TikTok Open API (OAuth).</div>
    </div>
  );
}

function InstagramAnalyticsPanel({ igAnalytics, igAnalyticsError, onLoad }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      onLoad?.();
    }
  }, [loaded]);
  if (igAnalyticsError) return <div style={{padding:12}}>
    <div style={{color:'#ff8484'}}>{igAnalyticsError}</div>
    <div style={{fontSize:12, opacity:.8, marginTop:6}}>Tipp: Stelle sicher, dass Instagram (Meta) verbunden ist und die richtige Facebook‑Seite mit Business/Creator‑Konto verknüpft ist.</div>
  </div>;
  if (!igAnalytics) return <div style={{padding:12, color:'#9aa3b2'}}>Analytics werden geladen …</div>;
  const totals = igAnalytics.totals || { likes: 0, comments: 0 };
  const profile = igAnalytics.profile || {};
  return (
    <div>
      <div style={{display:'flex', gap:16, flexWrap:'wrap', marginBottom:12}}>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(221,42,123,0.12)', border:'1px solid rgba(221,42,123,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Summe Likes</div>
          <div style={{fontWeight:800, fontSize:22}}>{totals.likes}</div>
        </div>
        <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(221,42,123,0.12)', border:'1px solid rgba(221,42,123,0.35)', borderRadius:12, padding:12}}>
          <div style={{opacity:.8, fontSize:13}}>Kommentare</div>
          <div style={{fontWeight:800, fontSize:22}}>{totals.comments}</div>
        </div>
      </div>
      <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>
        {typeof profile.followers_count === 'number' && (
          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:12}}>
            <div style={{opacity:.8, fontSize:13}}>Follower</div>
            <div style={{fontWeight:800, fontSize:22}}>{profile.followers_count}</div>
          </div>
        )}
        {typeof profile.media_count === 'number' && (
          <div style={{flex:'1 1 220px', minWidth:200, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:12}}>
            <div style={{opacity:.8, fontSize:13}}>Medien gesamt</div>
            <div style={{fontWeight:800, fontSize:22}}>{profile.media_count}</div>
          </div>
        )}
      </div>
      <div style={{fontSize:11, opacity:.75, marginTop:6}}>Datenquelle: Instagram Graph API (OAuth via Meta).</div>
    </div>
  );
}
